import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Package } from 'lucide-react';
import { Category } from '@/services/productsService';
import Image from '@/components/ui/Image';
import { placeholders } from '@/utils/placeholders';
import { cn, UPLOADS_URL } from '@/utils/utils';

interface CategoryCardProps {
    category: Category;
    variant?: 'default' | 'compact' | 'large';
    showProductCount?: boolean;
    showDescription?: boolean;
    className?: string;
    index?: number;
    products_count?: number;

}

const CategoryCard: React.FC<CategoryCardProps> = ({
    category,
    variant = 'default',
    showProductCount = false,
    showDescription = false,
    className,
    index = 0,
    //products_count = 0,
}) => {
    const variants = {
        compact: {
            container: 'p-3',
            image: 'w-12 h-12',
            title: 'text-sm font-medium',
            description: 'text-xs',
        },
        default: {
            container: 'p-4',
            image: 'w-16 h-16',
            title: 'text-base font-medium',
            description: 'text-sm',
        },
        large: {
            container: 'p-6',
            image: 'w-20 h-20',
            title: 'text-lg font-semibold',
            description: 'text-base',
        },
    };

    const variantStyles = variants[variant];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className={cn(className)}
        >
            <Link
                to={`/category/${category.id}`}
                className={cn(
                    'group block bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all duration-300 hover:border-primary-300 min-h-[200px]',
                    variantStyles.container
                )}
            >
                {variant === 'large' ? (
                    // Layout horizontal para variante large
                    <div className="flex items-stretch space-x-4 h-full">
                        <div className="flex-shrink-0">
                            <div className={cn(
                                'bg-primary-100 rounded-full flex items-center justify-center group-hover:bg-primary-200 transition-colors',
                                variantStyles.image
                            )}>
                                <Image
                                    src={UPLOADS_URL + category.image}
                                    alt={category.name}
                                    fallback={placeholders.category}
                                    className="object-contain"
                                    showPlaceholder={false}
                                />
                            </div>
                        </div>


                        <div className="flex-1 min-w-0">
                            <h3 className={cn(
                                variantStyles.title,
                                'text-gray-900 group-hover:text-primary-600 transition-colors mb-1'
                            )}>
                                {category.name}
                            </h3>

                            {showDescription && category.description && (
                                <p className={cn(variantStyles.description, 'text-gray-600 line-clamp-2 mb-2')}>
                                    {category.description}
                                </p>
                            )}

                            {showProductCount && (
                                <div className="flex items-center text-gray-500">
                                    <Package className="w-4 h-4 mr-1" />
                                    <span className="text-xs">
                                        {category.products_count || 0} productos
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex-shrink-0">
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors" />
                        </div>
                    </div>
                ) : (
                    // Layout vertical para compact y default
                    <div className="text-center">
                        <div className={cn(
                            'mx-auto mb-3 bg-primary-100 rounded-full flex items-center justify-center group-hover:bg-primary-200 transition-colors',
                            variantStyles.image
                        )}>
                            <Image
                                src={UPLOADS_URL + category.image}
                                alt={category.name}
                                fallback={placeholders.category}
                                className="object-contain"
                                showPlaceholder={false}
                            />
                        </div>


                        <h3 className={cn(
                            variantStyles.title,
                            'text-gray-900 group-hover:text-primary-600 transition-colors mb-1'
                        )}>
                            {category.name}
                        </h3>

                        {showDescription && category.description && (
                            <p className={cn(variantStyles.description, 'text-gray-600 line-clamp-2 mb-2')}>
                                {category.description}
                            </p>
                        )}

                        {showProductCount && (
                            <div className="flex items-center justify-center text-gray-500 mt-2">
                                <Package className="w-3 h-3 mr-1" />
                                <span className="text-xs">
                                    {category.products_count || 0}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </Link>
        </motion.div>
    );
};

export default CategoryCard;